import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, Lightbulb } from 'lucide-react';
import { blogPosts } from '../data/blogData';

const Blog = () => {
  const decorTips = [
    "Layer different textures for visual interest",
    "Use odd numbers when grouping decorative items",
    "Match your table runner to 1-2 colors from your palette",
    "Place cushions in varying sizes",
    "Rotate seasonal decor to keep your space fresh"
  ];

  return (
    <div className="min-h-screen bg-[#FEFCF9]">
      {/* Hero */}
      <section className="py-24 bg-[#F5EFE6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-xs tracking-[0.3em] text-[#C4A265] uppercase font-medium">Inspiration & Ideas</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-[#3E2723] mt-3 mb-6">The Behat House Journal</h1>
            <div className="w-12 h-px bg-[#C4A265] mb-6"></div>
            <p className="text-lg text-[#7A6B5D] leading-relaxed">
              Discover the stories behind our crafts, get expert decor tips, and find inspiration for transforming your space.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Tips */}
      <section className="py-12 bg-[#FEFCF9] border-b border-[#E8DCC8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-3 mb-6">
            <Lightbulb className="w-5 h-5 text-[#C4A265]" />
            <h2 className="text-xl font-serif text-[#3E2723]">Quick Decor Tips</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {decorTips.map((tip, index) => (
              <div key={index} className="bg-[#F5EFE6] p-4 border border-[#E8DCC8] hover:border-[#C4A265]/40 transition-colors">
                <span className="inline-flex items-center justify-center w-7 h-7 bg-[#3E2723] text-[#FEFCF9] text-xs font-bold mb-3">{index + 1}</span>
                <p className="text-sm text-[#7A6B5D]">{tip}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-serif text-[#3E2723] mb-4">Latest Articles</h2>
            <div className="w-12 h-px bg-[#C4A265] mx-auto mb-4"></div>
            <p className="text-[#7A6B5D] max-w-2xl mx-auto">Expert advice on creating beautiful living spaces</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <Link to={`/blog/${post.id}`} key={post.id} className="bg-[#FEFCF9] overflow-hidden border border-[#E8DCC8] hover:border-[#C4A265]/40 transition-colors group block">
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute top-4 left-4">
                    <span className="inline-flex items-center px-3 py-1 bg-[#FEFCF9]/95 text-xs font-medium text-[#C4A265] tracking-wider uppercase">
                      <post.icon className="w-3 h-3 mr-1" />{post.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center space-x-4 text-xs text-[#A09486] mb-3">
                    <span className="flex items-center"><Calendar className="w-3 h-3 mr-1" />{post.date}</span>
                    <span className="flex items-center"><Clock className="w-3 h-3 mr-1" />{post.readTime}</span>
                  </div>
                  <h3 className="text-lg font-serif text-[#3E2723] mb-3 group-hover:text-[#C4A265] transition-colors">{post.title}</h3>
                  <p className="text-[#7A6B5D] text-sm leading-relaxed mb-4">{post.excerpt}</p>
                  <span className="inline-flex items-center text-[#C4A265] font-medium hover:text-[#A0822E] transition-colors tracking-wider uppercase text-sm">
                    Read More<ArrowRight className="w-4 h-4 ml-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#F5EFE6]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-serif text-[#3E2723] mb-4">Ready to Transform Your Space?</h2>
          <div className="w-12 h-px bg-[#C4A265] mx-auto mb-6"></div>
          <p className="text-[#7A6B5D] mb-8">Browse our collection of handcrafted home decor.</p>
          <Link to="/shop" className="inline-flex items-center bg-[#3E2723] text-[#FEFCF9] px-8 py-3.5 font-semibold hover:bg-[#C4A265] transition-colors space-x-2 tracking-wider uppercase text-sm">
            <span>Explore Collection</span><ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Blog;
